import * as React from "react";
import { cn } from "@/lib/utils";

export interface SocialLinkFieldProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "children"> {
  /**
   * Icon component to display on the left side
   * @example <EmailIcon size={16} />
   */
  leftIcon?: React.ReactNode;
  /**
   * Text content to display (e.g., email address, social handle)
   */
  text: string;
  /**
   * Link destination (defaults to the text if not provided)
   */
  href?: string;
}

/**
 * Non-editable social link field with icon (clickable link variant)
 *
 * This component displays social network links as clickable anchors with icons.
 * Unlike SocialLinkInput, this is read-only and meant for display purposes.
 * Links open in a new tab by default for better UX.
 * Follows ANYW3AR Figma design specifications (node-id: 260:2271).
 *
 * @example
 * ```tsx
 * <SocialLinkField
 *   leftIcon={<EmailIcon size={16} />}
 *   text="contact@anyw3ar.com"
 *   href="mailto:contact@anyw3ar.com"
 * />
 * ```
 */
export const SocialLinkField = React.forwardRef<
  HTMLAnchorElement,
  SocialLinkFieldProps
>(
  (
    {
      className,
      leftIcon,
      text,
      href,
      target = "_blank",
      rel = "noopener noreferrer",
      ...props
    },
    ref
  ) => {
    // Default href based on text if not provided
    const defaultHref = href || text;

    return (
      <a
        ref={ref}
        href={defaultHref}
        target={target}
        rel={rel}
        className={cn(
          "flex items-center justify-between",
          "px-4 py-1 rounded-[0.3125rem]",
          "bg-white border border-gray-100",
          "transition-colors cursor-pointer",
          "hover:border-gray-300 hover:bg-gray-50",
          "focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-1",
          "active:bg-gray-100",
          className
        )}
        {...props}
      >
        {/* Left side: Icon + Text */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Left Icon Container */}
          {leftIcon && (
            <div className="flex items-center justify-center py-2 shrink-0">
              <span className="text-gray-100 uppercase">{leftIcon}</span>
            </div>
          )}

          {/* Text Display */}
          <div className="flex-1 min-w-0 flex items-center justify-center">
            <p className="font-montserrat text-[0.9375rem] font-normal leading-5 text-gray-900 truncate">
              {text}
            </p>
          </div>
        </div>
      </a>
    );
  }
);

SocialLinkField.displayName = "SocialLinkField";
